/**
 * OpenAI Assistants API Setup
 * Toutes les opérations passent par le backend (clé OpenAI côté serveur uniquement).
 */

export interface AssistantSetupResult {
  assistantId: string;
  vectorStoreId: string;
  filesUploaded: string[];
  success: boolean;
  message: string;
}

/**
 * ÉTAPE 1: Upload les fichiers knowledge base à OpenAI (via backend)
 *
 * La liste et le contenu des fichiers viennent du backend (public/knowledge sur le serveur),
 * afin qu'en production la synchro utilise toujours les fichiers déployés sur le serveur,
 * et non une liste/cache côté client qui peut être obsolète.
 */
export async function uploadKnowledgeFiles(): Promise<string[]> {
  // Récupérer la liste des fichiers .md depuis le serveur (source de vérité)
  const listResponse = await fetch('/api/prisme/assistant/knowledge-files', { credentials: 'include' });
  if (!listResponse.ok) {
    const err = await listResponse.json().catch(() => ({ error: listResponse.statusText }));
    throw new Error(`Impossible de récupérer la liste des fichiers knowledge: ${err.error || listResponse.statusText}`);
  }
  const { files: fileNames } = (await listResponse.json()) as { files: string[] };
  if (!fileNames?.length) {
    throw new Error('Aucun fichier .md trouvé dans public/knowledge côté serveur');
  }

  console.log(`📋 ${fileNames.length} fichier(s) knowledge à synchroniser (source: serveur)`);
  const uploadedFileIds: string[] = [];

  for (const fileName of fileNames) {
    try {
      console.log(`📄 Chargement: ${fileName}`);
      const fileResponse = await fetch(`/api/prisme/assistant/knowledge-file/${encodeURIComponent(fileName)}`, {
        credentials: 'include',
      });
      if (!fileResponse.ok) {
        throw new Error(`Fichier ${fileName} introuvable (status: ${fileResponse.status})`);
      }
      const content = await fileResponse.text();
      if (!content || content.length === 0) {
        throw new Error(`Fichier ${fileName} est vide`);
      }
      console.log(`📦 Contenu chargé: ${fileName} (${content.length} caractères)`);

      const uploadResponse = await fetch('/api/prisme/assistant/upload-file', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ fileContent: content, fileName }),
      });

      if (!uploadResponse.ok) {
        const error = await uploadResponse.json().catch(() => ({ error: uploadResponse.statusText }));
        throw new Error(`Upload failed for ${fileName}: ${error.error || uploadResponse.statusText}`);
      }

      const uploadData = await uploadResponse.json();
      uploadedFileIds.push(uploadData.id);
      console.log(`✅ Uploaded: ${fileName} → ${uploadData.id}`);
    } catch (error) {
      console.error(`❌ Error uploading ${fileName}:`, error);
      throw error;
    }
  }

  return uploadedFileIds;
}

/**
 * ÉTAPE 2: Créer un Vector Store avec les fichiers
 */
export async function createVectorStore(fileIds: string[]): Promise<string> {
  const response = await fetch('/api/prisme/assistant/create-vector-store', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({ fileIds }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Erreur inconnue' }));
    throw new Error(error.error || 'Erreur lors de la création du Vector Store');
  }

  const data = await response.json();
  console.log(`✅ Vector Store created: ${data.id}`);
  
  return data.id;
}

/**
 * ÉTAPE 3: Créer l'Assistant PRIsME avec File Search (via backend)
 */
export async function createPRIsMEAssistant(vectorStoreId: string): Promise<string> {
  const response = await fetch('/api/prisme/assistant/create', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({ vectorStoreId }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Erreur inconnue' }));
    throw new Error(error.error || 'Erreur lors de la création de l\'Assistant');
  }

  const data = await response.json();
  console.log(`✅ Assistant created: ${data.id}`);
  
  return data.id;
}

/**
 * Supprimer l'ancien assistant et Vector Store (si existants)
 */
async function cleanupOldAssistant(): Promise<void> {
  try {
    // Récupérer les IDs existants depuis la config serveur
    const configResponse = await fetch('/api/prisme/assistant/config', {
      credentials: 'include',
    });

    if (configResponse.ok) {
      const config = await configResponse.json();
      const oldAssistantId = config.assistant_id || config.assistantId;
      const oldVectorStoreId = config.vector_store_id || config.vectorStoreId;

      if (oldAssistantId || oldVectorStoreId) {
        console.log('🗑️ Suppression de l\'ancien assistant et Vector Store...');
        
        // Supprimer l'ancien assistant
        if (oldAssistantId) {
          try {
            await fetch('/api/prisme/assistant/delete', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              credentials: 'include',
              body: JSON.stringify({ assistantId: oldAssistantId }),
            });
            console.log(`✅ Ancien assistant ${oldAssistantId} supprimé`);
          } catch (err) {
            console.warn(`⚠️ Erreur suppression assistant ${oldAssistantId}:`, err);
          }
        }

        // Supprimer l'ancien Vector Store
        if (oldVectorStoreId) {
          try {
            await fetch('/api/prisme/assistant/delete-vector-store', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              credentials: 'include',
              body: JSON.stringify({ vectorStoreId: oldVectorStoreId }),
            });
            console.log(`✅ Ancien Vector Store ${oldVectorStoreId} supprimé`);
          } catch (err) {
            console.warn(`⚠️ Erreur suppression Vector Store ${oldVectorStoreId}:`, err);
          }
        }
      }
    }
  } catch (error) {
    console.warn('⚠️ Erreur lors du nettoyage de l\'ancien assistant:', error);
    // Ne pas faire échouer le setup si le nettoyage échoue
  }
}

/**
 * FONCTION PRINCIPALE: Setup complet de l'Assistant PRIsME
 * 
 * Supprime l'ancien assistant et Vector Store, puis crée les nouveaux avec tous les fichiers de connaissance.
 * Les IDs retournés doivent être sauvegardés (localStorage ou env).
 */
export async function setupPRIsMEAssistant(): Promise<AssistantSetupResult> {
  try {
    console.log('🚀 Début setup PRIsME Assistant...');

    // Étape 0: Nettoyer l'ancien assistant et Vector Store
    await cleanupOldAssistant();

    // Étape 1: Upload fichiers
    console.log('📤 Upload knowledge files...');
    const fileIds = await uploadKnowledgeFiles();
    console.log(`✅ ${fileIds.length} fichiers uploadés`);

    // Étape 2: Créer Vector Store
    console.log('🗂️ Création Vector Store...');
    const vectorStoreId = await createVectorStore(fileIds);

    // Étape 3: Créer Assistant
    console.log('🤖 Création Assistant...');
    const assistantId = await createPRIsMEAssistant(vectorStoreId);

    console.log('✅ Setup complet!');
    console.log(`📋 Assistant ID: ${assistantId}`);
    console.log(`📋 Vector Store ID: ${vectorStoreId}`);

    // Sauvegarder IDs dans localStorage (cache rapide pour ce navigateur)
    localStorage.setItem('prisme_assistant_id', assistantId);
    localStorage.setItem('prisme_vector_store_id', vectorStoreId);

    // Sauvegarder aussi côté serveur pour que tous les navigateurs/utilisateurs puissent les utiliser
    try {
      const saveResponse = await fetch('/api/prisme/assistant/config', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ assistantId, vectorStoreId }),
      });

      if (!saveResponse.ok) {
        const error = await saveResponse.json().catch(() => ({ error: saveResponse.statusText }));
        console.warn('⚠️ Impossible de sauvegarder la config Assistant côté serveur:', error.error || saveResponse.statusText);
      } else {
        console.log('✅ Config Assistant PRIsME sauvegardée côté serveur');
      }
    } catch (err) {
      console.warn('⚠️ Erreur lors de la sauvegarde serveur de la config Assistant:', err);
    }

    return {
      assistantId,
      vectorStoreId,
      filesUploaded: fileIds,
      success: true,
      message: 'Assistant PRIsME créé avec succès!'
    };
  } catch (error) {
    console.error('❌ Erreur setup:', error);
    return {
      assistantId: '',
      vectorStoreId: '',
      filesUploaded: [],
      success: false,
      message: error instanceof Error ? error.message : 'Erreur inconnue'
    };
  }
}

/**
 * Récupérer les IDs sauvegardés
 */
export function getAssistantIds(): { assistantId: string | null; vectorStoreId: string | null } {
  return {
    assistantId: localStorage.getItem('prisme_assistant_id'),
    vectorStoreId: localStorage.getItem('prisme_vector_store_id')
  };
}

/**
 * Vérifier si l'Assistant est configuré
 */
export function isAssistantConfigured(): boolean {
  const { assistantId } = getAssistantIds();
  return !!assistantId;
}

